/**
 * App — Root component
 *
 * Sets up:
 * - TanStack Query provider
 * - React Router 7 data router (all routes from FRONTEND.md §2)
 * - Global error toast listener (5xx → toast)
 * - Auth boot (silent refresh on load)
 */

import React, { useEffect, useLayoutEffect, useRef, useState, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { createBrowserRouter, RouterProvider, Outlet, Navigate, NavLink, useLocation, useNavigate, useParams, useRouteError } from 'react-router';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ApiClientError, classifyApiError, newIdempotencyKey } from '@budgeting101/api-client';
import {
  Button,
  Field,
  FinancialDataState,
  Modal,
  PermissionDenied,
  TextInput,
} from '@budgeting101/ui-web';
import { queryClient } from './lib/queryClient.js';
import {
  attemptSilentRefresh,
  useAuth,
  useAuthStore,
  type ActiveWorkspaceBook,
  type WorkspaceCapability,
} from './hooks/useAuth.js';
import { usePermissions } from './hooks/usePermissions.js';
import { nextThemePreference, useThemeStore } from './store/themeStore.js';
import { api, apiArraySchema, apiObjectSchema } from './lib/api.js';
import { scopedQueryKey } from './lib/queryKeys.js';
import {
  hasControlProfile,
  isMoneyOnlyProfile,
  isSharedOnlyProfile,
  WorkspaceContextGate,
} from './components/WorkspaceContextGate.js';
import { WorkspaceDeepLink } from './components/WorkspaceDeepLink.js';
import {
  SharedGroupSwitcher,
  useSharedNavigationModel,
} from './components/SharedNavigation.js';
import {
  MONEY_NAV_ITEMS,
  moneyMobileMoreItems,
  moneyMobilePrimaryItems,
  type MoneyNavigationItem,
} from './components/MoneyNavigation.js';
import { RouteAccessibility } from './components/RouteAccessibility.js';
import { LanguageSelect } from './components/LanguageSelect.js';
import { enablePush, iosNeedsInstall, pushSupported, readPushState } from './lib/push.js';
import { useLanguageStore } from './store/languageStore.js';
import { t } from '@budgeting101/i18n';

// ── Lazy route imports ────────────────────────────────────────────────────────
const LoginRoute        = React.lazy(() => import('./routes/login.js'));
const SsoRoute          = React.lazy(() => import('./routes/sso.js'));
const StartRoute          = React.lazy(() => import('./routes/start.js'));
const SharedOnboardingRoute = React.lazy(() => import('./routes/shared-onboarding.js'));
const MoneyOnboardingRoute = React.lazy(() => import('./routes/money-onboarding.js'));
const VerifyEmailRoute      = React.lazy(() => import('./routes/verify-email.js'));
const SharedJoinRoute       = React.lazy(() => import('./routes/shared-join.js'));
const SharedGuestRoute      = React.lazy(() => import('./routes/shared-guest.js'));
const RegisterRoute       = React.lazy(() => import('./routes/register.js'));
const ForgotPasswordRoute = React.lazy(() => import('./routes/forgot-password.js'));
const ResetPasswordRoute  = React.lazy(() => import('./routes/reset-password.js'));
const AcceptInviteRoute   = React.lazy(() => import('./routes/accept-invite.js'));
const DashboardRoute    = React.lazy(() => import('./routes/dashboard.js'));
const TransactionsRoute = React.lazy(() => import('./routes/transactions.js'));
const RecordRoute       = React.lazy(() => import('./routes/record.js'));
const ApprovalsRoute    = React.lazy(() => import('./routes/approvals.js'));
const AccountsRoute     = React.lazy(() => import('./routes/accounts.js'));
const CustomersRoute    = React.lazy(() => import('./routes/customers.js'));
const InvoicesRoute     = React.lazy(() => import('./routes/invoices.js'));
const BudgetsRoute      = React.lazy(() => import('./routes/budgets.js'));
const BudgetDetailRoute = React.lazy(() => import('./routes/budget-detail.js'));
const ReportsRoute      = React.lazy(() => import('./routes/reports.js'));
const SettleUpRoute     = React.lazy(() => import('./routes/settle-up.js'));
const SharedFoundationRoute = React.lazy(() => import('./routes/shared-foundation.js'));
const SharedGroupCreateRoute = React.lazy(() => import('./routes/shared-group-create.js'));
const SharedGroupDetailRoute = React.lazy(() => import('./routes/shared-group-detail.js'));
const MoneyHomeRoute = React.lazy(() => import('./routes/money/home.js'));
const MoneyRecordChooserRoute = React.lazy(() => import('./routes/money/record.js'));
const MoneyRecordEntryRoute = React.lazy(() => import('./routes/money/record-entry.js'));
const MoneyRecordExchangeRoute = React.lazy(() => import('./routes/money/record-exchange.js'));
const MoneyRecordAdjustRoute = React.lazy(() => import('./routes/money/record-adjust.js'));
const MoneyCounterpartyRoute = React.lazy(() => import('./routes/money/counterparty.js'));
const MoneyHistoryRoute = React.lazy(() => import('./routes/money/history.js'));
const MoneyServicesRoute = React.lazy(() => import('./routes/money/services.js'));
const MoneyTeamRoute = React.lazy(() => import('./routes/money/team.js'));
const AdminRoute = React.lazy(() => import('./routes/admin.js'));

// Per-user device preferences. Not a settings sub-route: /settings is gated on
// administrator permissions a Member does not hold.
const NotificationsRoute = React.lazy(() => import('./routes/notifications.js'));

// Settings sub-routes
const SettingsLayout    = React.lazy(() => import('./routes/settings/index.js'));
const SettingsCompany   = React.lazy(() => import('./routes/settings/company.js'));
const SettingsMembers   = React.lazy(() => import('./routes/settings/members.js'));
const SettingsRoles     = React.lazy(() => import('./routes/settings/roles.js'));
const SettingsCategories    = React.lazy(() => import('./routes/settings/categories.js'));
const SettingsCurrencies    = React.lazy(() => import('./routes/settings/currencies.js'));
const SettingsApprovalRules = React.lazy(() => import('./routes/settings/approval-rules.js'));
const SettingsAuditLog      = React.lazy(() => import('./routes/settings/audit-log.js'));
const SettingsIntegrations  = React.lazy(() => import('./routes/settings/integrations.js'));

// ── Route skeleton (loading state per FRONTEND.md §4) ────────────────────────
function PageSkeleton() {
  const profileKinds = useAuthStore((state) => state.profileKinds);
  const { pathname } = useLocation();
  const isCompanySurface = pathname === '/'
    || /^\/(?:record|settle-up|transactions|approvals|accounts|customers|invoices|budgets|reports|settings)(?:\/|$)/.test(pathname);
  const className = hasControlProfile(profileKinds) && isCompanySurface
    ? 'company-page company-page--wide page-skeleton'
    : 'page-shell page-skeleton';
  return (
    <div className={className}>
      {/* Title skeleton */}
      <div style={{ height: '30px', width: '200px', backgroundColor: 'var(--color-bg-strong)', borderRadius: 'var(--radius-sm)' }} />
      {/* Content skeletons */}
      {[1,2,3,4].map(i => (
        <div key={i} style={{ height: '56px', backgroundColor: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)' }} />
      ))}
    </div>
  );
}

// ── Auth guard ────────────────────────────────────────────────────────────────
function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isTransitioning = useAuthStore((s) => s.isTransitioning);
  const workspaceId = useAuthStore((s) => s.workspaceId);
  const platformAdmin = useAuthStore((s) => s.platformAdmin);
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (isAuthenticated) { setChecking(false); return; }
    attemptSilentRefresh().finally(() => setChecking(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (checking || isTransitioning) return <PageSkeleton />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (workspaceId === null) {
    // Identity-only session: no workspace exists to render. Onboarding owns
    // this state — except for platform administrators, whose console is
    // workspace-neutral by design.
    if (platformAdmin) {
      if (!location.pathname.startsWith('/admin')) return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/start" replace />;
    }
  }
  return <>{children}</>;
}

function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const platformAdmin = useAuthStore((state) => state.platformAdmin);
  if (!platformAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// ── App shell layout (responsive: sidebar ≥900px, bottom tab bar below) ───────

// 16px stroke icons — crisp geometry beats font glyphs at small sizes.
interface NavItem {
  href: string;
  label: string;
  labelKey?: string;
  icon: string;
  group?: 'Overview' | 'Money' | 'Operations' | 'Insight' | 'Administration';
  end?: boolean;
  anyPermission?: readonly string[];
  anyCapability?: readonly string[];
}

const NAV_ITEMS: NavItem[] = [
  { href: '/',              label: 'Dashboard',    labelKey: 'nav.dashboard', icon: 'dashboard', group: 'Overview', anyCapability: ['finance.ledger'] },
  { href: '/record',        label: 'Record',       labelKey: 'nav.record', icon: 'add_circle', group: 'Money', anyPermission: ['transactions.record'], anyCapability: ['finance.transactions'] },
  { href: '/transactions',  label: 'Transactions', labelKey: 'nav.transactions', icon: 'receipt_long', group: 'Money', anyPermission: ['reports.view-all', 'reports.view-own', 'transactions.record'], anyCapability: ['finance.transactions'] },
  { href: '/accounts',      label: 'Accounts',     labelKey: 'nav.accounts', icon: 'account_balance', group: 'Money', anyCapability: ['finance.accounts'] },
  { href: '/settle-up',     label: 'Settle Up',    labelKey: 'nav.settleUp', icon: 'swap_horiz', group: 'Money', anyPermission: ['transactions.record'], anyCapability: ['finance.settlement'] },
  { href: '/customers',     label: 'Customers',    labelKey: 'nav.customers', icon: 'group', group: 'Operations', anyCapability: ['parties.customers'] },
  { href: '/invoices',      label: 'Invoices',     labelKey: 'nav.invoices', icon: 'request_quote', group: 'Operations', anyPermission: ['reports.view-all', 'reports.view-own', 'transactions.record'], anyCapability: ['finance.invoicing'] },
  { href: '/budgets',       label: 'Budgets',      labelKey: 'nav.budgets', icon: 'account_balance_wallet', group: 'Operations', anyPermission: ['budgets.view', 'budgets.manage'], anyCapability: ['finance.budgets'] },
  { href: '/approvals',     label: 'Approvals',    labelKey: 'nav.approvals', icon: 'verified', group: 'Operations', anyPermission: ['approvals.decide'], anyCapability: ['finance.approvals'] },
  { href: '/reports',       label: 'Reports',      labelKey: 'nav.reports', icon: 'bar_chart', group: 'Insight', anyPermission: ['reports.view-all'], anyCapability: ['finance.reporting'] },
  { href: '/settings',      label: 'Settings',     labelKey: 'nav.settings', icon: 'settings', group: 'Administration', anyPermission: ['company.settings', 'members.manage', 'roles.manage', 'accounts.manage', 'rates.manage'], anyCapability: ['core.workspace', 'core.membership', 'finance.accounts', 'finance.fx', 'finance.approvals', 'core.audit'] },
];

const NAV_GROUP_LABELS: NonNullable<NavItem['group']>[] = ['Overview', 'Money', 'Operations', 'Insight', 'Administration'];
const NAV_GROUPS = NAV_GROUP_LABELS.map((label) => ({ label, items: NAV_ITEMS.filter((item) => item.group === label) }));

const GROUP_LABEL_KEYS: Record<string, string> = {
  Overview: 'nav.groupOverview',
  Money: 'nav.groupMoney',
  Operations: 'nav.groupOperations',
  Insight: 'nav.groupInsight',
  Administration: 'nav.groupAdministration',
  Workspace: 'nav.groupWorkspace',
  'Current group': 'nav.groupCurrentGroup',
};

const TAB_ITEMS = NAV_ITEMS.filter((item) => ['/', '/record', '/transactions'].includes(item.href));
const MORE_ITEMS = NAV_ITEMS.filter((item) => !TAB_ITEMS.includes(item));
const READABLE_CAPABILITY_STATES = new Set(['READY', 'SUSPENDED', 'ARCHIVED']);

function hasReadableCapability(
  capabilities: readonly WorkspaceCapability[],
  keys: readonly string[],
): boolean {
  return keys.some((key) => capabilities.some((capability) => (
    capability.key === key && READABLE_CAPABILITY_STATES.has(capability.state)
  )));
}

function canViewItem(item: NavItem, permissions: string[], capabilities: readonly WorkspaceCapability[]) {
  const permitted = !item.anyPermission
    || item.anyPermission.some((permission) => permissions.includes(permission));
  const capable = !item.anyCapability || hasReadableCapability(capabilities, item.anyCapability);
  return permitted && capable;
}

function localizeItem<T extends NavItem>(item: T): T {
  return item.labelKey ? { ...item, label: t(item.labelKey) } : item;
}

function localizeMoneyItem(item: MoneyNavigationItem): NavItem & { shortLabel: string } {
  return {
    href: item.href,
    label: t(item.labelKey),
    labelKey: item.labelKey,
    shortLabel: t(item.shortLabelKey ?? item.labelKey),
    icon: item.icon,
    end: item.end,
    anyPermission: item.anyPermission,
    anyCapability: ['money.ledger'],
  };
}

function controlCapabilitiesForPath(pathname: string): readonly string[] | null {
  if (pathname === '/') return ['finance.ledger'];
  if (pathname.startsWith('/record')) return ['finance.transactions'];
  if (pathname.startsWith('/settle-up')) return ['finance.settlement'];
  if (pathname.startsWith('/customers')) return ['parties.customers'];
  if (pathname.startsWith('/invoices')) return ['finance.invoicing'];
  if (pathname.startsWith('/transactions')) return ['finance.transactions'];
  if (pathname.startsWith('/approvals')) return ['finance.approvals'];
  if (pathname.startsWith('/accounts')) return ['finance.accounts'];
  if (pathname.startsWith('/budgets')) return ['finance.budgets'];
  if (pathname.startsWith('/reports')) return ['finance.reporting'];
  if (pathname.startsWith('/settings/company')) return ['core.workspace'];
  if (pathname.startsWith('/settings/members') || pathname.startsWith('/settings/roles')) {
    return ['core.membership'];
  }
  if (pathname.startsWith('/settings/categories')) return ['finance.accounts'];
  if (pathname.startsWith('/settings/currencies')) return ['finance.fx'];
  if (pathname.startsWith('/settings/approval-rules')) return ['finance.approvals'];
  if (pathname.startsWith('/settings/audit-log')) return ['core.audit'];
  if (pathname.startsWith('/settings/integrations')) return ['integrations.crm'];
  if (pathname === '/settings' || pathname === '/settings/') {
    return ['core.workspace', 'core.membership', 'finance.accounts', 'finance.fx'];
  }
  return null;
}

function RequirePermission({ anyOf, children }: { anyOf: string[]; children: React.ReactNode }) {
  const { canAny } = usePermissions();
  if (!canAny(...anyOf)) {
    return (
      <div className="company-page company-page--wide">
        <PermissionDenied
          subject={t('ui.thisPage')}
          requiredPermission={anyOf.join(' or ')}
          explanation={t('ui.requirePermissionExplanation')}
          actions={<NavLink to="/">{t('ui.backToDashboard')}</NavLink>}
        />
      </div>
    );
  }
  return <>{children}</>;
}

function RequireAllPermissions({ allOf, children }: { allOf: string[]; children: React.ReactNode }) {
  const { can } = usePermissions();
  if (!allOf.every((permission) => can(permission))) {
    return (
      <div className="page-shell">
        <PermissionDenied
          subject={t('ui.thisPage')}
          requiredPermission={allOf.join(' and ')}
          explanation={t('ui.requirePermissionExplanation')}
          actions={<NavLink to="/">{t('ui.backToDashboard')}</NavLink>}
        />
      </div>
    );
  }
  return <>{children}</>;
}

function MoneyRecordKindRoute() {
  const { kind } = useParams();
  if (kind === 'send' || kind === 'receive') {
    return <Suspense fallback={<PageSkeleton />}><MoneyRecordEntryRoute /></Suspense>;
  }
  if (kind === 'exchange') {
    return <Suspense fallback={<PageSkeleton />}><MoneyRecordExchangeRoute /></Suspense>;
  }
  if (kind === 'adjust') {
    return (
      <RequireAllPermissions allOf={['money.entries.record', 'money.entries.void']}>
        <Suspense fallback={<PageSkeleton />}><MoneyRecordAdjustRoute /></Suspense>
      </RequireAllPermissions>
    );
  }
  return <Navigate to="/money/record" replace />;
}

export function SettingsIndexRedirect() {
  const { can } = usePermissions();
  const capabilities = useAuthStore((state) => state.capabilities);
  const destination = [
    ['company.settings', '/settings/company', 'core.workspace'],
    ['members.manage', '/settings/members', 'core.membership'],
    ['roles.manage', '/settings/roles', 'core.membership'],
    ['accounts.manage', '/settings/categories', 'finance.accounts'],
    ['rates.manage', '/settings/currencies', 'finance.fx'],
    ['roles.manage', '/settings/approval-rules', 'finance.approvals'],
    ['company.settings', '/settings/audit-log', 'core.audit'],
  ].find(([permission, , capability]) => (
    can(permission) && hasReadableCapability(capabilities, [capability])
  ));
  return <Navigate to={destination?.[1] ?? '/'} replace />;
}

function WorkspaceHome() {
  const profileKinds = useAuthStore((state) => state.profileKinds);
  if (isMoneyOnlyProfile(profileKinds)) {
    return <Suspense fallback={<PageSkeleton />}><MoneyHomeRoute /></Suspense>;
  }
  if (isSharedOnlyProfile(profileKinds)) {
    return <Suspense fallback={<PageSkeleton />}><SharedFoundationRoute /></Suspense>;
  }
  if (hasControlProfile(profileKinds)) {
    return <Suspense fallback={<PageSkeleton />}><DashboardRoute /></Suspense>;
  }
  return (
    <div className="page-shell">
      <PermissionDenied
        subject={t('ui.thisWorkspaceProfile')}
        explanation={t('ui.workspaceHomeExplanation')}
      />
    </div>
  );
}

/** Prevent non-Control profiles from ever mounting Company-oriented routes. */
export function WorkspaceProfileOutlet() {
  const profileKinds = useAuthStore((state) => state.profileKinds);
  const capabilities = useAuthStore((state) => state.capabilities);
  const location = useLocation();
  const control = hasControlProfile(profileKinds);
  const sharedOnly = isSharedOnlyProfile(profileKinds);
  const moneyOnly = isMoneyOnlyProfile(profileKinds);
  const sharedRoute = location.pathname.startsWith('/shared/');
  const moneyRoute = location.pathname.startsWith('/money/');
  // Device notification preferences belong to the person, not to a workspace
  // profile, so every profile kind must be able to reach them.
  const preferencesRoute = location.pathname.startsWith('/notifications');
  if (sharedRoute && !sharedOnly) return <Navigate to="/" replace />;
  if (moneyRoute && !moneyOnly) return <Navigate to="/" replace />;
  if (!control && location.pathname !== '/' && !sharedRoute && !moneyRoute && !preferencesRoute) {
    return <Navigate to="/" replace />;
  }
  if (sharedRoute && !hasReadableCapability(capabilities, ['shared.groups'])) {
    return (
      <div className="page-shell">
        <FinancialDataState
          status="unavailable"
          label={t('ui.sharedGroupsCapability')}
          error={{
            title: t('auth.sharedGroupsUnavailable'),
            message: t('auth.sharedGroupsUnexposed'),
          }}
        />
      </div>
    );
  }
  if (moneyOnly && !hasReadableCapability(capabilities, ['money.ledger'])) {
    return (
      <div className="page-shell">
        <FinancialDataState
          status="unavailable"
          label={t('ui.moneyWorkspace')}
          error={{
            title: t('auth.capabilityUnavailable'),
            message: t('auth.capabilityUnexposed'),
          }}
        />
      </div>
    );
  }
  const requiredCapabilities = controlCapabilitiesForPath(location.pathname);
  if (control && requiredCapabilities && !hasReadableCapability(capabilities, requiredCapabilities)) {
    return (
      <div className="page-shell">
        <FinancialDataState
          status="unavailable"
          label={t('ui.workspaceCapability')}
          error={{
            title: t('auth.capabilityUnavailable'),
            message: t('auth.capabilityUnexposed'),
          }}
        />
      </div>
    );
  }
  return (
    <WorkspaceContextGate>
      <WorkspaceDeepLink />
      {control && <InstallAppPrompt />}
      <Outlet />
    </WorkspaceContextGate>
  );
}

function AppShell() {
  const profileKinds = useAuthStore((state) => state.profileKinds);
  const control = hasControlProfile(profileKinds);
  return (
    <div className="app-shell" data-workspace={control ? 'control' : undefined}>
      <RouteAccessibility />
      <a className="skip-link" href="#main-content">{t('nav.skipToMain')}</a>
      <Sidebar />
      <main className="app-main" id="main-content" tabIndex={-1}>
        <WorkspaceProfileOutlet />
      </main>
      {/* Outside the profile gate: every profile kind has devices. */}
      <NotificationPrompt />
      <TabBar />
    </div>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const INSTALL_DISMISS_KEY = 'ledger-install-prompt-dismissed-at';
const INSTALL_DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

export function InstallAppPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [eligibleDevice, setEligibleDevice] = useState(false);
  const valueQuery = useQuery({
    queryKey: scopedQueryKey(['install-prompt', 'delivered-value']),
    queryFn: () => api.get(
      '/transactions',
      apiArraySchema<{ id: string }>(),
      { limit: 1 },
    ),
    enabled: eligibleDevice,
    staleTime: 5 * 60_000,
  });
  const hasDeliveredValue = (valueQuery.data?.length ?? 0) > 0;

  useEffect(() => {
    const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
    const standalone = window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
    const mobile = window.innerWidth <= 899 || window.matchMedia('(pointer: coarse)').matches;
    const dismissedAt = Number(localStorage.getItem(INSTALL_DISMISS_KEY) ?? '0');
    const recentlyDismissed = Number.isFinite(dismissedAt) && Date.now() - dismissedAt < INSTALL_DISMISS_MS;
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIos(ios);

    if (standalone || !mobile || recentlyDismissed) {
      setEligibleDevice(false);
      return;
    }
    setEligibleDevice(true);

    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    };
    const handleInstalled = () => {
      setVisible(false);
      setInstallEvent(null);
      setEligibleDevice(false);
      localStorage.removeItem(INSTALL_DISMISS_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    document.documentElement.dataset.installPromptReady = 'true';
    return () => {
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      delete document.documentElement.dataset.installPromptReady;
    };
  }, []);

  useEffect(() => {
    if (!eligibleDevice || !hasDeliveredValue) return;
    // iOS has no beforeinstallprompt event; reveal instructions only after a
    // confirmed financial record proves that the product has delivered value.
    if (isIos) {
      const revealTimer = window.setTimeout(() => {
        setShowInstructions(true);
        setVisible(true);
      }, 1200);
      return () => window.clearTimeout(revealTimer);
    }
    // Chromium's event can fire before the value query resolves. Retain it,
    // then reveal only when both installability and post-value eligibility are true.
    if (installEvent) setVisible(true);
  }, [eligibleDevice, hasDeliveredValue, installEvent, isIos]);

  useEffect(() => {
    if (!eligibleDevice) return;
    const recheckDeliveredValue = () => { void valueQuery.refetch(); };
    window.addEventListener('finance:command-confirmed', recheckDeliveredValue);
    return () => window.removeEventListener('finance:command-confirmed', recheckDeliveredValue);
  }, [eligibleDevice, valueQuery.refetch]);

  function dismiss() {
    localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }

  async function install() {
    if (!installEvent) {
      setShowInstructions(true);
      return;
    }
    const event = installEvent;
    // Remove our UI before Chrome opens its own prompt. This guarantees that
    // a dismissed, failed, or slow native prompt cannot leave a touch-blocking
    // surface over the finance pages.
    setVisible(false);
    setInstallEvent(null);
    try {
      await event.prompt();
      const choice = await event.userChoice;
      if (choice.outcome === 'dismissed') dismiss();
    } catch {
      setShowInstructions(true);
      setVisible(true);
    }
  }

  function finishInstructions() {
    if (showInstructions) {
      dismiss();
    }
  }

  if (!visible) return null;

  return (
    <aside className="app-install-prompt" aria-label={t('ui.installApp')} aria-live="polite">
      <div className="app-install-icon" aria-hidden="true"><LogoMark size={30} /></div>
      <div className="app-install-copy">
        <strong>{showInstructions ? t('ui.addLedgerToHome') : t('ui.useLedgerLikeApp')}</strong>
        <span>
          {showInstructions
            ? isIos
              ? t('ui.tapShareAddHome')
              : t('ui.openMenuInstallApp')
            : t('ui.saveToHomeForFast')}
        </span>
      </div>
      <div className="app-install-actions">
        {!showInstructions && <button className="app-install-primary" type="button" onClick={() => void install()}>{t('ui.installApp')}</button>}
        <button className="app-install-secondary" type="button" onClick={showInstructions ? finishInstructions : dismiss}>{showInstructions ? t('ui.gotIt') : t('ui.notNow')}</button>
      </div>
    </aside>
  );
}

/** SVG logo mark — golden rectangle (42×26px, ratio ≈ φ) with geometric "LM" monogram. */
function LogoMark({ size = 42 }: { size?: number }) {
  const h = Math.round(size / 1.618);
  return (
    <svg width={size} height={h} viewBox="0 0 42 26" fill="none" aria-hidden="true" style={{ display: 'block' }}>
      {/* Golden rectangle outline */}
      <rect x="0.5" y="0.5" width="41" height="25" rx="3" stroke="currentColor" strokeWidth="1.2" fill="none" />
      {/* φ vertical divider at 61.8% */}
      <line x1="25.6" y1="1" x2="25.6" y2="25" stroke="currentColor" strokeWidth="0.6" opacity="0.3" />
      {/* L glyph — left partition (major segment) */}
      <path d="M8 7v12h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      {/* M glyph — right partition (minor segment) */}
      <path d="M28 19V7l3.5 7L35 7v12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

const WORKSPACE_CURRENCIES = ['AED', 'AUD', 'CAD', 'CHF', 'DKK', 'EUR', 'GBP', 'JPY', 'NOK', 'SEK', 'USD'];

type CreatableWorkspaceKind = 'SHARED' | 'CONTROL';

function detectedWorkspaceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function workspaceProfileLabel(profileKinds: readonly string[]): string {
  if (profileKinds.includes('OPERATIONS')) return t('workspaceCreate.operationsName');
  if (profileKinds.includes('CONTROL')) return t('workspaceCreate.controlName');
  if (profileKinds.includes('MONEY')) return t('workspaceCreate.moneyName');
  if (profileKinds.includes('SHARED')) return t('workspaceCreate.sharedName');
  return t('ui.ledgerWorkspace');
}

export function CreateWorkspaceDialog({
  open,
  onClose,
  onCreated,
  onStartMoney,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (workspaceId: string) => Promise<void>;
  onStartMoney: () => void;
}) {
  const [kind, setKind] = useState<CreatableWorkspaceKind | null>(null);
  const [name, setName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [timezone, setTimezone] = useState(detectedWorkspaceTimezone);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setKind(null);
    setError('');
  }, [open]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!kind) return;
    setBusy(true);
    setError('');
    try {
      const shared = kind === 'SHARED';
      const result = await api.post(
        shared ? '/workspaces/shared' : '/workspaces',
        apiObjectSchema<{ workspace: { id: string } }>(),
        shared
          ? {
              name: name.trim(),
              groupName: groupName.trim(),
              summaryCurrency: currency,
              timezone: timezone.trim(),
            }
          : { name: name.trim(), baseCurrency: currency, timezone: timezone.trim() },
        { idempotencyKey: newIdempotencyKey() },
      );
      await onCreated(result.workspace.id);
      setName('');
      setGroupName('');
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('workspaceCreate.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title={kind ? t('workspaceCreate.configureTitle', { type: workspaceProfileLabel([kind]) }) : t('workspaceCreate.title')}
      description={kind === 'SHARED'
        ? t('workspaceCreate.sharedDescription')
        : kind === 'CONTROL'
          ? t('workspaceCreate.controlDescription')
          : t('workspaceCreate.description')}
      size={kind ? 'sm' : 'md'}
      footer={(
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={() => { if (kind) { setKind(null); setError(''); } else onClose(); }}
            disabled={busy}
          >
            {kind ? t('common.back') : t('common.cancel')}
          </Button>
          {kind ? (
            <Button type="submit" form="create-workspace" loading={busy}>
              {kind === 'SHARED' ? t('workspaceCreate.confirmShared') : t('workspaceCreate.confirmControl')}
            </Button>
          ) : null}
        </>
      )}
    >
      {!kind ? (
        <div className="app-workspace-kind-grid">
          <button type="button" className="app-workspace-kind" onClick={() => setKind('SHARED')}>
            <span className="material-symbols-outlined" aria-hidden="true">group</span>
            <span className="app-workspace-kind-copy">
              <strong>{t('workspaceCreate.sharedName')}</strong>
              <span>{t('workspaceCreate.sharedSummary')}</span>
            </span>
            <small>{t('workspaceCreate.availableNow')}</small>
          </button>
          <button type="button" className="app-workspace-kind" onClick={() => setKind('CONTROL')}>
            <span className="material-symbols-outlined" aria-hidden="true">account_balance</span>
            <span className="app-workspace-kind-copy">
              <strong>{t('workspaceCreate.controlName')}</strong>
              <span>{t('workspaceCreate.controlSummary')}</span>
            </span>
            <small>{t('workspaceCreate.availableNow')}</small>
          </button>
          <button
            type="button"
            className="app-workspace-kind"
            onClick={() => { onClose(); onStartMoney(); }}
          >
            <span className="material-symbols-outlined" aria-hidden="true">currency_exchange</span>
            <span className="app-workspace-kind-copy">
              <strong>{t('workspaceCreate.moneyName')}</strong>
              <span>{t('workspaceCreate.moneySummary')}</span>
            </span>
            <small>{t('workspaceCreate.availableNow')}</small>
          </button>
          <button type="button" className="app-workspace-kind app-workspace-kind--upcoming" disabled>
            <span className="material-symbols-outlined" aria-hidden="true">factory</span>
            <span className="app-workspace-kind-copy">
              <strong>{t('workspaceCreate.operationsName')}</strong>
              <span>{t('workspaceCreate.operationsSummary')}</span>
            </span>
            <small>{t('workspaceCreate.comingSoon')}</small>
          </button>
          <p className="app-workspace-kind-boundary">
            <span className="material-symbols-outlined" aria-hidden="true">shield_lock</span>
            {t('workspaceCreate.isolation')}
          </p>
        </div>
      ) : (
        <form id="create-workspace" className="app-workspace-create-form" onSubmit={(event) => void submit(event)}>
          <Field label={t('workspaceCreate.name')} required>
            {(props) => (
              <TextInput
                {...props}
                value={name}
                maxLength={120}
                autoComplete="organization"
                onChange={(event) => setName(event.target.value)}
                required
              />
            )}
          </Field>
          {kind === 'SHARED' ? (
            <Field label={t('workspaceCreate.groupName')} required>
              {(props) => (
                <TextInput
                  {...props}
                  value={groupName}
                  maxLength={120}
                  onChange={(event) => setGroupName(event.target.value)}
                  required
                />
              )}
            </Field>
          ) : null}
          <Field label={kind === 'SHARED' ? t('workspaceCreate.summaryCurrency') : t('workspaceCreate.currency')} required>
            {(props) => (
              <select
                {...props}
                className="ui-input"
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                required
              >
                {WORKSPACE_CURRENCIES.map((candidate) => <option key={candidate} value={candidate}>{candidate}</option>)}
              </select>
            )}
          </Field>
          <Field label={t('workspaceCreate.timezone')} helperText={t('workspaceCreate.timezoneHelp')} required>
            {(props) => (
              <TextInput
                {...props}
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                required
              />
            )}
          </Field>
          <p className="app-workspace-create-effect">
            <span className="material-symbols-outlined" aria-hidden="true">info</span>
            {kind === 'SHARED' ? t('workspaceCreate.sharedEffect') : t('workspaceCreate.controlEffect')}
          </p>
          {error ? <p className="app-workspace-error" role="alert">{error}</p> : null}
        </form>
      )}
    </Modal>
  );
}

export function WorkspaceSwitcher() {
  const navigate = useNavigate();
  const {
    workspaceId,
    workspaceName,
    companyName,
    workspaces,
    switchWorkspace,
  } = useAuth();
  const [open, setOpen]   = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');
  const [menuPosition, setMenuPosition] = useState({ blockStart: 0, inlineStart: 0, triggerWidth: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = React.useId();

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!open || !trigger || !menu) return;

    const triggerBounds = trigger.getBoundingClientRect();
    const menuBounds = menu.getBoundingClientRect();
    const isRtl = getComputedStyle(trigger).direction === 'rtl';
    const preferredInlineStart = isRtl
      ? window.innerWidth - triggerBounds.right
      : triggerBounds.left;
    const inlineStart = Math.max(0, Math.min(preferredInlineStart, window.innerWidth - menuBounds.width));
    const nextPosition = {
      blockStart: triggerBounds.bottom,
      inlineStart,
      triggerWidth: triggerBounds.width,
    };
    setMenuPosition((current) => (
      current.blockStart === nextPosition.blockStart
      && current.inlineStart === nextPosition.inlineStart
      && current.triggerWidth === nextPosition.triggerWidth
        ? current
        : nextPosition
    ));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      const active = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"][aria-checked="true"]');
      const first = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"]');
      (active ?? first)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeMenu(true);
    };
    const handleResize = () => closeMenu();
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  function closeMenu(restoreFocus = false) {
    setOpen(false);
    if (restoreFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function openMenu() {
    const trigger = triggerRef.current;
    if (trigger) {
      const bounds = trigger.getBoundingClientRect();
      const isRtl = getComputedStyle(trigger).direction === 'rtl';
      setMenuPosition({
        blockStart: bounds.bottom,
        inlineStart: isRtl ? window.innerWidth - bounds.right : bounds.left,
        triggerWidth: bounds.width,
      });
    }
    setOpen(true);
  }

  function handleMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const options = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]:not(:disabled), [role="menuitem"]:not(:disabled)') ?? [],
    );
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeMenu(true);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || options.length === 0) return;
    event.preventDefault();
    const current = options.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? options.length - 1
        : event.key === 'ArrowDown'
          ? (current + 1 + options.length) % options.length
          : (current - 1 + options.length) % options.length;
    options[next]?.focus();
  }

  const label = workspaceName ?? companyName ?? t('ui.ledgerWorkspace');

  async function choose(id: string) {
    if (id === workspaceId) { closeMenu(true); return; }
    setBusy(true); setError('');
    try {
      await switchWorkspace(id);
      closeMenu();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function startCreate() {
    closeMenu();
    setCreateOpen(true);
  }

  return (
    <div className="app-workspace">
      <button
        ref={triggerRef}
        type="button"
        className="app-workspace-trigger"
        onClick={() => {
          if (open) closeMenu();
          else openMenu();
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            openMenu();
          }
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        disabled={busy}
      >
        <span className="app-workspace-name">{label}</span>
        <span className="material-symbols-outlined app-workspace-caret" aria-hidden="true">
          {busy ? 'progress_activity' : 'unfold_more'}
        </span>
      </button>

      {open && (
        createPortal(<>
          <div className="app-workspace-backdrop" aria-hidden="true" onClick={() => closeMenu()} />
          <div
            ref={menuRef}
            id={menuId}
            className="app-workspace-menu"
            role="menu"
            aria-label={t('nav.switchWorkspace')}
            onKeyDown={handleMenuKeyDown}
            style={{
              '--app-workspace-menu-block-start': `${menuPosition.blockStart}px`,
              '--app-workspace-menu-inline-start': `${menuPosition.inlineStart}px`,
              '--app-workspace-trigger-width': `${menuPosition.triggerWidth}px`,
            } as React.CSSProperties}
          >
            {workspaces.map((w) => (
              <button
                key={w.workspaceId}
                type="button"
                role="menuitemradio"
                aria-checked={w.workspaceId === workspaceId}
                className={`app-workspace-option${w.workspaceId === workspaceId ? ' app-workspace-option--active' : ''}`}
                onClick={() => void choose(w.workspaceId)}
                disabled={busy}
              >
                <span className="app-workspace-copy">
                  <span className="app-workspace-option-name">{w.workspaceName}</span>
                  <span className="app-workspace-role">{workspaceProfileLabel(w.profileKinds)} · {w.roleName}</span>
                </span>
                {w.workspaceId === workspaceId && (
                  <span className="material-symbols-outlined" aria-hidden="true">check</span>
                )}
              </button>
            ))}
            <button
              type="button"
              role="menuitem"
              className="app-workspace-option app-workspace-option--create"
              onClick={startCreate}
              disabled={busy}
            >
              <span className="material-symbols-outlined" aria-hidden="true">add</span>
              <span className="app-workspace-copy">
                <span className="app-workspace-option-name">{t('workspaceCreate.action')}</span>
                <span className="app-workspace-description">{t('workspaceCreate.chooseProfile')}</span>
              </span>
            </button>
            {error && <p className="app-workspace-error" role="alert">{error}</p>}
          </div>
        </>, triggerRef.current?.closest('.app-shell') ?? document.body)
      )}
      <CreateWorkspaceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={switchWorkspace}
        onStartMoney={() => navigate('/onboarding/money')}
      />
    </div>
  );
}

function BookContext({ activeBook }: { activeBook: ActiveWorkspaceBook | null }) {
  const detail = activeBook
    ? `${activeBook.status.toLowerCase()}${activeBook.currency ? ` · ${activeBook.currency}` : ''}`
    : t('ui.financialRoutesUnavailable');
  const detailClassName = activeBook
    ? 'app-book-context-detail app-book-context-status'
    : 'app-book-context-detail app-book-context-message';

  return (
    <div className="app-book-context">
      <span className="material-symbols-outlined" aria-hidden="true">menu_book</span>
      <span>
        <strong>{activeBook?.name ?? t('ui.noBookSelected')}</strong>
        <small className={detailClassName}>{detail}</small>
      </span>
    </div>
  );
}

export function Sidebar() {
  const {
    userName,
    userEmail,
    role,
    activeBook,
    profileKinds,
    capabilities,
    platformAdmin,
    logout,
  } = useAuth();
  const { permissions } = usePermissions();
  const control = hasControlProfile(profileKinds);
  const money = isMoneyOnlyProfile(profileKinds);
  const sharedOnly = isSharedOnlyProfile(profileKinds);
  const sharedNavigation = useSharedNavigationModel(sharedOnly);
  const availableGroups = control
    ? NAV_GROUPS.map((group) => ({ ...group, items: group.items.map(localizeItem) }))
    : money
      ? [{ label: 'Workspace', items: MONEY_NAV_ITEMS.map(localizeMoneyItem) }]
      : [
        { label: 'Workspace', items: sharedNavigation.workspaceItems },
        ...(sharedNavigation.currentGroupId
          ? [{ label: 'Current group', items: sharedNavigation.groupItems }]
          : []),
      ];
  const groups = availableGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canViewItem(item, permissions, capabilities)),
    }))
    .filter((group) => group.items.length > 0);
  const initials = (userName || userEmail || 'Ledger User')
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  return (
    <nav aria-label="Main navigation" className="app-sidebar">
      <div className="app-brand">
        <span className="app-logo-frame"><LogoMark size={30} /></span>
        <div>
          <p className="app-brand-name">Ledger Mono</p>
          <span className="app-brand-kicker">{control ? t('ui.controlWorkspace') : money ? t('ui.moneyWorkspace') : t('ui.sharedWorkspace')}</span>
        </div>
      </div>

      {sharedOnly && (
        <div className="shared-sidebar-context" aria-label={t('ui.sharedWorkspace')}>
          <p className="app-nav-label">{t('ui.ledgerWorkspace')}</p>
          <WorkspaceSwitcher />
          <SharedGroupSwitcher model={sharedNavigation} />
          {sharedNavigation.groupItems.some((item) => item.key === 'record') && (
            <NavLink
              className="shared-sidebar-primary"
              to={sharedNavigation.groupItems.find((item) => item.key === 'record')!.href}
            >
              <span className="material-symbols-outlined" aria-hidden="true">add</span>
              <span>{t('nav.recordExpense')}</span>
            </NavLink>
          )}
        </div>
      )}

      {money && (
        <div className="shared-sidebar-context money-sidebar-context" aria-label={t('ui.moneyWorkspace')}>
          <p className="app-nav-label">{t('ui.moneyWorkspace')}</p>
          <WorkspaceSwitcher />
          {permissions.includes('money.entries.record') ? (
            <NavLink className="shared-sidebar-primary" to="/money/record">
              <span className="material-symbols-outlined" aria-hidden="true">add_circle</span>
              <span>{t('money.nav.record')}</span>
            </NavLink>
          ) : null}
        </div>
      )}

      {control && (
        <div className="app-control-context" aria-label={t('ui.activeContext')}>
          <p className="app-nav-label">{t('ui.activeContext')}</p>
          <WorkspaceSwitcher />
          <BookContext activeBook={activeBook} />
        </div>
      )}

      <div className="app-nav-scroll">
        {groups.map((group) => {
          const labelId = `nav-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          return (
            <section className="app-nav-group" key={group.label} aria-labelledby={labelId}>
              <p className="app-nav-label" id={labelId}>{t(GROUP_LABEL_KEYS[group.label] ?? group.label)}</p>
              {group.items.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.end ?? item.href === '/'}
                  className={`app-navlink${control && item.href === '/record' ? ' app-navlink--record' : ''}`}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </section>
          );
        })}
      </div>

      <div className="app-sidebar-footer">
        {platformAdmin ? (
          <NavLink className="app-admin-link" to="/admin">
            <span className="material-symbols-outlined" aria-hidden="true">admin_panel_settings</span>
            <span>{t('admin.openConsole')}</span>
          </NavLink>
        ) : null}
        <div className="app-utility-row">
          <LanguageSelect compact />
          <ThemeToggle />
          <NotificationsLink compact />
        </div>
        <div className="app-user-card">
          <span className="app-avatar" aria-hidden="true">{initials || 'LU'}</span>
          <span className="app-user-copy">
            <span className="app-user-name">{userName || userEmail || 'Ledger user'}</span>
            <span className="app-user-role">{role || t('nav.defaultRole')}</span>
          </span>
          <button className="app-utility-button" type="button" onClick={() => void logout()} aria-label={t('auth.logout')} title={t('auth.logout')}>
            <span className="material-symbols-outlined" aria-hidden="true">logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
}

const NOTIFY_PROMPT_DISMISS_KEY = 'ledger-notify-prompt-dismissed';
const NOTIFY_PROMPT_DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * First-run invitation to turn notifications on.
 *
 * This is a SOFT prompt, and the distinction matters: it is our own UI, not the
 * browser's. Calling Notification.requestPermission() on load would be the
 * classic mistake — a browser permanently blocks a site once its prompt is
 * dismissed, and script can never ask again. So the real prompt fires only from
 * the Enable button, and someone who taps "Not now" can still be asked later.
 *
 * It reveals only once the server confirms push is actually configured, so the
 * button can never lead nowhere, and never stacks on the install prompt.
 */
export function NotificationPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem(NOTIFY_PROMPT_DISMISS_KEY) ?? '0');
    if (Number.isFinite(dismissedAt) && Date.now() - dismissedAt < NOTIFY_PROMPT_DISMISS_MS) return undefined;
    // 'granted' means already on; 'denied' means the browser will never ask again,
    // so nagging would be pure noise.
    if (!pushSupported() || iosNeedsInstall() || Notification.permission !== 'default') return undefined;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void readPushState()
        .then((state) => {
          if (cancelled || state !== 'disabled') return;
          if (document.querySelector('.app-install-prompt')) return;
          setVisible(true);
        })
        .catch(() => undefined);
    }, 2_000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  function dismiss() {
    localStorage.setItem(NOTIFY_PROMPT_DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }

  async function enable() {
    setBusy(true);
    // Remove our card before the browser opens its own prompt, so a slow or
    // dismissed native prompt cannot leave a surface over the finance pages.
    setVisible(false);
    try {
      await enablePush();
    } catch {
      // Silent: the settings page is the place that reports failures. A first-run
      // invitation must never turn into an error banner over someone's ledger.
    } finally {
      setBusy(false);
    }
  }

  if (!visible) return null;

  return (
    <aside
      className="app-notify-prompt"
      aria-label={t('notifications.enable.title')}
      aria-live="polite"
      data-testid="notification-prompt"
    >
      <div className="app-install-icon" aria-hidden="true">
        <span className="material-symbols-outlined">notifications</span>
      </div>
      <div className="app-install-copy">
        <strong>{t('notifications.enable.title')}</strong>
        <span>{t('notifications.enable.onlyPermitted')}</span>
      </div>
      <div className="app-install-actions">
        <button
          className="app-install-primary"
          type="button"
          disabled={busy}
          onClick={() => void enable()}
          data-testid="notification-prompt-enable"
        >
          {t('notifications.enable.enableCta')}
        </button>
        <button
          className="app-install-secondary"
          type="button"
          onClick={dismiss}
          data-testid="notification-prompt-dismiss"
        >
          {t('ui.notNow')}
        </button>
      </div>
    </aside>
  );
}

/**
 * Entry point to per-user device settings.
 *
 * Sits beside language and theme because it is the same class of thing: a
 * preference belonging to this person on this device, not to the workspace. It
 * carries no permission or capability gate — every role has devices.
 *
 * This link is the only way the notification permission prompt is ever reached.
 * The prompt is deliberately never fired on page load: a browser permanently
 * blocks a site once its prompt is dismissed, and script cannot ask again, so it
 * must originate from someone deciding to click.
 */
export function NotificationsLink({ compact = false }: { compact?: boolean }) {
  const label = t('notifications.enable.title');
  return (
    <NavLink
      className={compact ? 'app-utility-button' : 'app-more-utility-link'}
      to="/notifications"
      aria-label={label}
      title={label}
      // Distinct ids per surface: the desktop footer link stays in the DOM at
      // mobile widths (hidden by CSS), so a shared id matches twice.
      data-testid={compact ? 'notifications-link' : 'notifications-link-mobile'}
    >
      <span className="material-symbols-outlined" aria-hidden="true">notifications</span>
      {compact ? null : <span>{label}</span>}
    </NavLink>
  );
}

export function ThemeToggle() {
  const preference = useThemeStore((state) => state.preference);
  const resolved = useThemeStore((state) => state.resolved);
  const setPreference = useThemeStore((state) => state.setPreference);
  const nextPreference = nextThemePreference(preference, resolved);
  const resolvedLabel = resolved === 'dark' ? t('nav.themeDark') : t('nav.themeLight');
  const currentLabel = preference === 'system'
    ? t('nav.themeCurrentSystem', { resolved: resolvedLabel })
    : t(preference === 'dark' ? 'nav.themeDark' : 'nav.themeLight');
  const nextLabel = t(nextPreference === 'system' ? 'nav.themeSystem' : nextPreference === 'dark' ? 'nav.themeDark' : 'nav.themeLight');
  const toggleAria = t('nav.themeToggleAriaLabel', { current: currentLabel, next: nextLabel });
  return (
    <button
      className="app-utility-button"
      type="button"
      data-theme-preference={preference}
      onClick={() => setPreference(nextPreference)}
      aria-label={toggleAria}
      title={toggleAria}
    >
      <span className="material-symbols-outlined" aria-hidden="true">
        {preference === 'system' ? 'brightness_auto' : resolved === 'dark' ? 'light_mode' : 'dark_mode'}
      </span>
    </button>
  );
}


export function TabBar() {
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreSearch, setMoreSearch] = useState('');
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const moreCloseRef = useRef<HTMLButtonElement>(null);
  const morePanelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { permissions } = usePermissions();
  const profileKinds = useAuthStore((state) => state.profileKinds);
  const capabilities = useAuthStore((state) => state.capabilities);
  const activeBook = useAuthStore((state) => state.activeBook);
  const platformAdmin = useAuthStore((state) => state.platformAdmin);
  const control = hasControlProfile(profileKinds);
  const money = isMoneyOnlyProfile(profileKinds);
  const sharedOnly = isSharedOnlyProfile(profileKinds);
  const sharedNavigation = useSharedNavigationModel(sharedOnly);
  const tabItems = control
    ? TAB_ITEMS.filter((item) => canViewItem(item, permissions, capabilities)).map(localizeItem)
    : money
      ? moneyMobilePrimaryItems.map(localizeMoneyItem).filter((item) => canViewItem(item, permissions, capabilities))
      : sharedNavigation.mobilePrimaryItems;
  const moreItems = control
    ? MORE_ITEMS.filter((item) => canViewItem(item, permissions, capabilities)).map(localizeItem)
    : money
      ? moneyMobileMoreItems.map(localizeMoneyItem).filter((item) => canViewItem(item, permissions, capabilities))
      : sharedNavigation.mobileMoreItems;
  const normalizedSearch = moreSearch.trim().toLocaleLowerCase();
  const visibleMoreItems = normalizedSearch
    ? moreItems.filter((item) => item.label.toLocaleLowerCase().includes(normalizedSearch))
    : moreItems;
  const moreActive = moreItems.some((item) => (
    item.end || item.href === '/'
      ? location.pathname === item.href
      : location.pathname.startsWith(item.href)
  ));

  useEffect(() => {
    setMoreOpen(false);
    setMoreSearch('');
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const background = Array.from(document.querySelectorAll<HTMLElement>('.app-main, .app-sidebar, .app-tabbar'));
    background.forEach((element) => { element.inert = true; });
    moreCloseRef.current?.focus();
    function handlePanelKeyDown(event: KeyboardEvent) {
      const nestedLayerOpen = document.querySelector(
        '.app-workspace-menu, [role="dialog"][aria-modal="true"]:not(#mobile-more-navigation)',
      );
      if (nestedLayerOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMore();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = morePanelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handlePanelKeyDown);
    return () => {
      document.removeEventListener('keydown', handlePanelKeyDown);
      background.forEach((element) => { element.inert = false; });
    };
  }, [moreOpen]);

  function closeMore() {
    setMoreOpen(false);
    setMoreSearch('');
    requestAnimationFrame(() => moreButtonRef.current?.focus());
  }

  return (
    <>
      {moreOpen && (
        <>
          <div className="app-more-backdrop" aria-hidden="true" onClick={closeMore} />
          <div
            ref={morePanelRef}
            className="app-more-panel"
            id="mobile-more-navigation"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-more-title"
          >
            <div className="app-more-heading">
              <div>
                <span className="app-more-kicker">{t('nav.groupWorkspace')}</span>
                <strong id="mobile-more-title">{t('nav.moreDestinations')}</strong>
              </div>
              <button ref={moreCloseRef} className="app-more-close" type="button" onClick={closeMore} aria-label={t('common.close')}>
                <span className="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </div>
            <div className="app-more-workspace">
              <LanguageSelect />
              {/* The sidebar footer holding the compact bell is desktop-only, so
                  without this the notification prompt is unreachable on a phone —
                  where it matters most. */}
              <NotificationsLink />
              {platformAdmin ? (
                <NavLink className="app-more-utility-link" to="/admin">
                  <span className="material-symbols-outlined" aria-hidden="true">admin_panel_settings</span>
                  <span>{t('admin.openConsole')}</span>
                </NavLink>
              ) : null}
              <WorkspaceSwitcher />
              {sharedOnly && <SharedGroupSwitcher model={sharedNavigation} />}
              {control ? <BookContext activeBook={activeBook} /> : null}
            </div>
            <label className="app-more-search">
              <span>{t('nav.findDestination')}</span>
              <span className="app-more-search-control">
                <span className="material-symbols-outlined" aria-hidden="true">search</span>
                <input
                  type="search"
                  value={moreSearch}
                  onChange={(event) => setMoreSearch(event.target.value)}
                  placeholder={t('nav.searchDestinations')}
                />
              </span>
            </label>
            <nav className="app-more-grid" aria-label={t('nav.moreDestinations')}>
              {visibleMoreItems.map((item) => (
                <NavLink key={item.href} to={item.href} end={item.end ?? item.href === '/'} className="app-more-link">
                  <span className="material-symbols-outlined" aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
            {visibleMoreItems.length === 0 ? <p className="app-more-empty" role="status">{t('nav.noDestinationMatch', { query: moreSearch.trim() })}</p> : null}
          </div>
        </>
      )}

      <nav aria-label="Main navigation" className="app-tabbar">
        {tabItems.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.end ?? item.href === '/'}
            className={`app-tab${money && item.href === '/money/record' ? ' app-tab--money-record' : ''}${control && item.href === '/record' ? ' app-tab--control-record' : ''}`}
          >
            <span className="material-symbols-outlined" aria-hidden="true">{item.icon}</span>
            <span>{!control && 'shortLabel' in item ? item.shortLabel : item.label}</span>
          </NavLink>
        ))}
        {(!control || moreItems.length > 0 || platformAdmin) && <button
          ref={moreButtonRef}
          type="button"
          className={`app-tab app-tab--button${moreActive || moreOpen ? ' app-tab--active' : ''}`}
          onClick={() => setMoreOpen((open) => !open)}
          aria-expanded={moreOpen}
          aria-controls={moreOpen ? 'mobile-more-navigation' : undefined}
        >
          <span className="material-symbols-outlined" aria-hidden="true">apps</span>
          <span>{t('nav.more')}</span>
        </button>}
      </nav>
    </>
  );
}

// ── Route error boundary ──────────────────────────────────────────────────────
// After a deploy, browsers holding the previous index.html request chunk hashes
// that no longer exist. That surfaces as a failed dynamic import — recoverable
// by reloading once (the fresh index.html references the new chunks).
function RouteErrorBoundary() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : String(error);
  const isStaleChunk = /dynamically imported module|Loading chunk|import/i.test(message);

  useEffect(() => {
    if (isStaleChunk && sessionStorage.getItem('chunk-reload') !== '1') {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
    }
  }, [isStaleChunk]);

  // Raw exception text stays in diagnostics; the screen only carries safe
  // copy and, when the failure came from the API, its reference ID.
  useEffect(() => {
    if (!isStaleChunk) console.error('Route error boundary:', error);
  }, [error, isStaleChunk]);

  if (isStaleChunk) return <PageSkeleton />; // reloading momentarily

  const referenceId = error instanceof ApiClientError ? error.apiError.referenceId : undefined;
  const offline = classifyApiError(error) === 'offline';

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-6)', backgroundColor: 'var(--color-bg)' }}>
      <div style={{ maxWidth: '420px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h1-size)', fontWeight: 700, color: 'var(--color-text)' }}>
          {offline ? t('errors.youAreOffline') : t('common.error')}
        </h1>
        <p style={{ margin: 0, fontSize: 'var(--text-body-size)', color: 'var(--color-text-secondary)' }}>
          {offline
            ? t('errors.pageRequiresConnection')
            : t('errors.pageCouldNotBeShown')}
        </p>
        {referenceId && (
          <p style={{ margin: 0, fontSize: 'var(--text-caption-size)', color: 'var(--color-text-secondary)', userSelect: 'all' }}>
            {t('ui.reference', { id: referenceId })}
          </p>
        )}
        <div>
          <button
            className="ui-btn"
            data-variant="primary"
            data-size="md"
            onClick={() => { sessionStorage.removeItem('chunk-reload'); window.location.assign('/'); }}
          >
            {t('nav.reloadApp')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Router definition ─────────────────────────────────────────────────────────
const router = createBrowserRouter([
  {
    path: '/start',
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <StartRoute />
      </Suspense>
    ),
  },
  {
    path: '/onboarding/shared',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><SharedOnboardingRoute /></Suspense>,
  },
  {
    path: '/onboarding/money',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><MoneyOnboardingRoute /></Suspense>,
  },
  {
    path: '/verify-email',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><VerifyEmailRoute /></Suspense>,
  },
  {
    path: '/shared/join',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><SharedJoinRoute /></Suspense>,
  },
  {
    path: '/join/shared-guest',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><SharedGuestRoute /></Suspense>,
  },
  {
    path: '/sso',
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <SsoRoute />
      </Suspense>
    ),
  },
  {
    path: '/login',
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <LoginRoute />
      </Suspense>
    ),
  },
  {
    path: '/register',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><RegisterRoute /></Suspense>,
  },
  {
    path: '/forgot-password',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><ForgotPasswordRoute /></Suspense>,
  },
  {
    path: '/reset-password',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><ResetPasswordRoute /></Suspense>,
  },
  {
    path: '/accept-invite',
    errorElement: <RouteErrorBoundary />,
    element: <Suspense fallback={<PageSkeleton />}><AcceptInviteRoute /></Suspense>,
  },
  {
    path: '/admin',
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireAuth>
        <RequirePlatformAdmin>
          <Suspense fallback={<PageSkeleton />}><AdminRoute /></Suspense>
        </RequirePlatformAdmin>
      </RequireAuth>
    ),
  },
  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: <WorkspaceHome />,
      },
      {
        path: 'shared/new',
        element: <Suspense fallback={<PageSkeleton />}><SharedGroupCreateRoute /></Suspense>,
      },
      {
        path: 'shared/groups/:groupId/*',
        element: <Suspense fallback={<PageSkeleton />}><SharedGroupDetailRoute /></Suspense>,
      },
      {
        path: 'money/record',
        element: <RequirePermission anyOf={['money.entries.record']}><Suspense fallback={<PageSkeleton />}><MoneyRecordChooserRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'money/record/:kind',
        element: <RequirePermission anyOf={['money.entries.record']}><MoneyRecordKindRoute /></RequirePermission>,
      },
      {
        path: 'money/counterparties/:id',
        element: <Suspense fallback={<PageSkeleton />}><MoneyCounterpartyRoute /></Suspense>,
      },
      {
        path: 'money/history',
        element: <Suspense fallback={<PageSkeleton />}><MoneyHistoryRoute /></Suspense>,
      },
      {
        path: 'money/services',
        element: <Suspense fallback={<PageSkeleton />}><MoneyServicesRoute /></Suspense>,
      },
      {
        path: 'money/team',
        element: <Suspense fallback={<PageSkeleton />}><MoneyTeamRoute /></Suspense>,
      },
      {
        path: 'settle-up',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><SettleUpRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'transactions',
        element: <RequirePermission anyOf={['reports.view-all', 'reports.view-own', 'transactions.record']}><Suspense fallback={<PageSkeleton />}><TransactionsRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'record',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><RecordRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'record/:kind',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><RecordRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'transactions/new',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><RecordRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'transactions/new/:kind',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><RecordRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'transactions/:id/edit',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><RecordRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'approvals',
        element: <RequirePermission anyOf={['approvals.decide']}><Suspense fallback={<PageSkeleton />}><ApprovalsRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'accounts',
        element: <Suspense fallback={<PageSkeleton />}><AccountsRoute /></Suspense>,
      },
      {
        path: 'accounts/:id',
        element: <Suspense fallback={<PageSkeleton />}><AccountsRoute /></Suspense>,
      },
      {
        path: 'customers',
        element: <Suspense fallback={<PageSkeleton />}><CustomersRoute /></Suspense>,
      },
      {
        path: 'customers/:id',
        element: <Suspense fallback={<PageSkeleton />}><CustomersRoute /></Suspense>,
      },
      {
        path: 'invoices',
        element: <RequirePermission anyOf={['reports.view-all', 'reports.view-own', 'transactions.record']}><Suspense fallback={<PageSkeleton />}><InvoicesRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'invoices/new',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><InvoicesRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'invoices/payment',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><InvoicesRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'invoices/:id',
        element: <RequirePermission anyOf={['reports.view-all', 'reports.view-own', 'transactions.record']}><Suspense fallback={<PageSkeleton />}><InvoicesRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'invoices/:id/edit',
        element: <RequirePermission anyOf={['transactions.record']}><Suspense fallback={<PageSkeleton />}><InvoicesRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'budgets',
        element: <RequirePermission anyOf={['budgets.view', 'budgets.manage']}><Suspense fallback={<PageSkeleton />}><BudgetsRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'budgets/:id',
        element: <RequirePermission anyOf={['budgets.view', 'budgets.manage']}><Suspense fallback={<PageSkeleton />}><BudgetDetailRoute /></Suspense></RequirePermission>,
      },
      {
        path: 'reports',
        element: <RequirePermission anyOf={['reports.view-all']}><Suspense fallback={<PageSkeleton />}><ReportsRoute /></Suspense></RequirePermission>,
      },
      {
        // No RequirePermission: every role has devices, so every role reaches
        // this. controlCapabilitiesForPath returns null for it, so no capability
        // gate applies either.
        path: 'notifications',
        element: <Suspense fallback={<PageSkeleton />}><NotificationsRoute /></Suspense>,
      },
      {
        path: 'settings',
        element: <RequirePermission anyOf={['company.settings', 'members.manage', 'roles.manage', 'accounts.manage', 'rates.manage']}><Suspense fallback={<PageSkeleton />}><SettingsLayout /></Suspense></RequirePermission>,
        children: [
          { index: true, element: <SettingsIndexRedirect /> },
          { path: 'company',    element: <RequirePermission anyOf={['company.settings']}><Suspense fallback={<PageSkeleton />}><SettingsCompany /></Suspense></RequirePermission> },
          { path: 'members',    element: <RequirePermission anyOf={['members.manage']}><Suspense fallback={<PageSkeleton />}><SettingsMembers /></Suspense></RequirePermission> },
          { path: 'roles',      element: <RequirePermission anyOf={['roles.manage']}><Suspense fallback={<PageSkeleton />}><SettingsRoles /></Suspense></RequirePermission> },
          { path: 'categories', element: <RequirePermission anyOf={['accounts.manage']}><Suspense fallback={<PageSkeleton />}><SettingsCategories /></Suspense></RequirePermission> },
          { path: 'currencies',      element: <RequirePermission anyOf={['rates.manage']}><Suspense fallback={<PageSkeleton />}><SettingsCurrencies /></Suspense></RequirePermission> },
          { path: 'approval-rules',  element: <RequirePermission anyOf={['roles.manage']}><Suspense fallback={<PageSkeleton />}><SettingsApprovalRules /></Suspense></RequirePermission> },
          { path: 'audit-log',       element: <RequirePermission anyOf={['company.settings']}><Suspense fallback={<PageSkeleton />}><SettingsAuditLog /></Suspense></RequirePermission> },
          { path: 'integrations',    element: <RequirePermission anyOf={['company.settings']}><Suspense fallback={<PageSkeleton />}><SettingsIntegrations /></Suspense></RequirePermission> },
        ],
      },
    ],
  },
]);

// ── Global command-failure surface ────────────────────────────────────────────
// Fallback for mutation failures that no caller handled contextually. Finance
// errors persist until dismissed (never a vanishing toast), carry the server
// reference ID, coalesce duplicates, and say truthfully whether anything was
// sent.
interface CommandFailureNotice {
  id: number;
  message: string;
  referenceId?: string;
  count: number;
}

function failureCopy(error: unknown): string {
  switch (classifyApiError(error)) {
    case 'offline':
      return 'You are offline. Nothing was sent — your draft is kept on this device.';
    case 'network':
      return 'The connection dropped while sending. The result is unknown — refresh the relevant list and check for the result before retrying.';
    default:
      return 'The server could not confirm the result. Refresh the relevant list and check for the result before retrying.';
  }
}

function ErrorToastProvider({ children }: { children: React.ReactNode }) {
  const [notices, setNotices] = useState<CommandFailureNotice[]>([]);

  useEffect(() => {
    function handleError(event: Event) {
      const error = (event as CustomEvent).detail;
      const message = failureCopy(error);
      const referenceId = error instanceof ApiClientError ? error.apiError.referenceId : undefined;

      setNotices((prev) => {
        const existing = prev.find((n) => n.message === message && n.referenceId === referenceId);
        if (existing) {
          return prev.map((n) => (n === existing ? { ...n, count: n.count + 1 } : n));
        }
        return [...prev, { id: Date.now() + prev.length, message, referenceId, count: 1 }];
      });
    }
    window.addEventListener('query:server-error', handleError);
    return () => window.removeEventListener('query:server-error', handleError);
  }, []);

  const dismiss = (id: number) => setNotices((prev) => prev.filter((n) => n.id !== id));

  return (
    <>
      {children}
      {notices.length > 0 && (
        <div
          className="app-toast-stack"
          role="alert"
          style={{
            position: 'fixed',
            bottom: 'var(--space-6)',
            insetInlineEnd: 'var(--space-6)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            maxWidth: 'min(360px, calc(100vw - var(--space-6) * 2))',
          }}
        >
          {notices.map((n) => (
            <div
              key={n.id}
              className="app-toast"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'var(--color-text)',
                color: 'var(--color-bg)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-body-size)',
                boxShadow: 'var(--shadow-overlay)',
                animation: 'slideInRight 200ms var(--motion-easing) both',
              }}
            >
              <span>
                {n.message}
                {n.count > 1 ? ` (×${n.count})` : ''}
              </span>
              {n.referenceId && (
                <span style={{ fontSize: 'var(--text-caption-size)', opacity: 0.8, userSelect: 'all' }}>
                  Reference {n.referenceId}
                </span>
              )}
              <button
                type="button"
                onClick={() => dismiss(n.id)}
                style={{
                  alignSelf: 'flex-end',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: 'var(--text-caption-size)',
                  color: 'inherit',
                  textDecoration: 'underline',
                  minWidth: '44px',
                  minHeight: '44px',
                  padding: 'var(--space-2)',
                  touchAction: 'manipulation',
                }}
              >
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes slideInRight { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </>
  );
}

// ── Root App component ────────────────────────────────────────────────────────
export function App() {
  const locale = useLanguageStore((state) => state.locale);
  // A successful boot means the current chunks loaded — re-arm the one-shot
  // stale-chunk reload so the *next* deploy can self-heal too.
  useEffect(() => {
    sessionStorage.removeItem('chunk-reload');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorToastProvider>
        <RouterProvider key={locale} router={router} />
      </ErrorToastProvider>
    </QueryClientProvider>
  );
}
